import type { GetTokenSilentlyOptions } from '@auth0/auth0-react';
import { AUTH_0_SCOPE } from 'constants/constants';

const audience = import.meta.env.VITE_AUTH_NAMESPACE as string;

const EXPIRES_GRACE_PERIOD = 60_000;
const BACKGROUND_RETRY_DELAY = 10_000;

const AUTH0_SESSION_ERRORS = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant',
]);

// Globals
let authParams: SetupParametersType | null = null;
let accessTokenCache: {
  token: string;
  expiresAt: number;
} | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
export let isRedirectingToLogin = false;

interface AccessTokenPayload {
  exp: number;
  [key: string]: unknown;
}

export type AuthScheme = 'Bearer';

export type SetupParametersType = {
  type: 'auth';
  getAccessTokenSilently: (options: GetTokenSilentlyOptions) => Promise<string | undefined>;
  redirectToLogin: () => Promise<void> | void;
};

const decodeToken = (token: string): AccessTokenPayload => {
  const [, payload] = token.split('.');
  return JSON.parse(atob(payload)) as AccessTokenPayload;
};

const isAuth0SessionError = (e: Record<string, unknown>): boolean =>
  e != null && typeof e.error === 'string' && AUTH0_SESSION_ERRORS.has(e.error);

const handleTokenRefreshError = (
  e: unknown,
  isSessionError: boolean,
  redirectToLogin: () => Promise<void> | void,
  controller: AbortController,
) => {
  const isTokenExpired = !accessTokenCache || accessTokenCache.expiresAt <= Date.now();

  if (isTokenExpired || isSessionError) {
    if (!isSessionError && isTokenExpired) {
      console.warn('Error while refreshing access token, aborting request. Error:', (e as Error).message);
    }

    void redirectToLogin();
    controller.abort();
  } else {
    console.warn('Error while refreshing access token, continuing with request. Error:', (e as Error).message);
  }
};

export const markRedirectingToLogin = () => {
  isRedirectingToLogin = true;
};

export const setAuthParams = (params: SetupParametersType) => {
  authParams = params;
  scheduleBackgroundRefresh(false);
};

const scheduleBackgroundRefresh = (retry: boolean) => {
  clearTimeout(refreshTimer);
  if (!accessTokenCache) return;

  const delay = retry
    ? BACKGROUND_RETRY_DELAY
    : Math.max(accessTokenCache.expiresAt - Date.now() - EXPIRES_GRACE_PERIOD, 0);

  refreshTimer = setTimeout(() => void refreshAccessToken(new AbortController()), delay);
};

export const refreshAccessToken = async (
  controller: AbortController,
  forceRefresh: boolean = false,
): Promise<{ token?: string; scheme: AuthScheme }> => {
  const params = authParams;
  if (!params) return { scheme: 'Bearer' };

  let accessToken = accessTokenCache?.token;
  const currentTime = Date.now();

  const isExpired =
    forceRefresh ||
    !accessToken ||
    !accessTokenCache ||
    accessTokenCache.expiresAt - currentTime <= EXPIRES_GRACE_PERIOD;

  if (!isExpired) {
    return { token: accessToken, scheme: 'Bearer' };
  }

  let refreshFailed = false;

  try {
    const newToken = await params.getAccessTokenSilently({
      timeoutInSeconds: 60,
      audience,
      scope: AUTH_0_SCOPE,
      cacheMode: 'off',
    } as GetTokenSilentlyOptions);

    if (newToken) {
      const { exp } = decodeToken(newToken);
      accessToken = newToken;
      accessTokenCache = { token: newToken, expiresAt: exp * 1_000 };
    }
  } catch (e) {
    refreshFailed = true;
    handleTokenRefreshError(e, isAuth0SessionError(e as Record<string, unknown>), params.redirectToLogin, controller);
  }

  scheduleBackgroundRefresh(refreshFailed);
  return { token: accessToken, scheme: 'Bearer' };
};

export const getAccessToken = async (forceRefresh: boolean = false): Promise<string | undefined> => {
  const { token } = await refreshAccessToken(new AbortController(), forceRefresh);
  if (!token) {
    console.warn('Failed to fetch access token; request will be sent without an authorization header');
  }
  return token;
};
